<template>
  <div class="article-time-picker">
    <time-picker-field
      v-if="hasAvailableTimes"
      v-model="timeValue"
      :config="pickerConfig"
      :available-times="availableTimes"
      legacy-format
    />
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { TimePickerField, createTimePickerConfig, formatLegacyTime, getCurrentTimeRounded, parseTime } from '@/time-picker';

export default {
  components: {
    TimePickerField,
  },

  props: ['itemProperty', 'properties'],

  computed: {
    s_item_property: {
      get() {
        return this.itemProperty;
      },

      set(val) {
        this.$emit('update:itemProperty', val);
      },
    },

    dateFormat() {
      return process.env.VUE_APP_SERVER.toLowerCase() === 'ru' ? 'DD-MM-YYYY' : 'YYYY-MM-DD';
    },

    minuteInterval() {
      if (
        this.s_item_property.extra
        && this.s_item_property.extra.time_interval !== undefined
        && this.s_item_property.extra.time_interval !== null
      ) {
        return parseInt(this.s_item_property.extra.time_interval, 10);
      }

      return 30;
    },

    availableTimes() {
      const range = this.getTimeRange();
      return Array.isArray(range) ? range : [];
    },

    hasAvailableTimes() {
      return this.availableTimes.length > 0;
    },

    timeValue: {
      get() {
        if (this.s_item_property.value) {
          return this.s_item_property.value;
        }

        return this.getDefaultTime();
      },

      set(value) {
        this.$set(this.itemProperty, 'value', value);
        this.$root.setPropertyToLocalStorage(this.itemProperty.id, value);
      },
    },

    pickerConfig() {
      const isRu = this.$root.currentLanguage === 'ru';
      const hotelColor = this.$root.hotelColor();

      return createTimePickerConfig({
        minuteInterval: this.minuteInterval,
        applyButtonColor: hotelColor,
        applyButtonHoverColor: hotelColor,
        placeholder: isRu ? 'Выберите время' : 'Select time',
        applyLabel: isRu ? 'Применить' : 'Apply',
        dropdownZIndex: 1100,
      });
    },
  },

  watch: {
    availableTimes() {
      this.syncValueWithRange();
    },
  },

  mounted() {
    this.syncValueWithRange();
  },

  methods: {
    getDefaultTime() {
      if (!this.availableTimes.length) {
        return '';
      }

      const { hour, minute } = parseTime(getCurrentTimeRounded(this.minuteInterval));
      const legacyCurrent = formatLegacyTime(hour, minute);

      if (this.availableTimes.includes(legacyCurrent)) {
        return legacyCurrent;
      }

      return this.findNearestAvailableTime(legacyCurrent);
    },

    findNearestAvailableTime(preferred) {
      const preferredMinutes = this.timeToMinutes(preferred);
      let nearest = this.availableTimes[this.availableTimes.length - 1];
      let nearestDiff = Infinity;

      this.availableTimes.forEach((slot) => {
        const diff = this.timeToMinutes(slot) - preferredMinutes;
        if (diff >= 0 && diff < nearestDiff) {
          nearestDiff = diff;
          nearest = slot;
        }
      });

      return nearest;
    },

    timeToMinutes(value) {
      const { hour, minute } = parseTime(value);
      return (hour * 60) + minute;
    },

    syncValueWithRange() {
      if (!this.hasAvailableTimes) {
        return;
      }

      const currentValue = this.s_item_property.value;
      if (!currentValue) {
        this.timeValue = this.getDefaultTime();
        return;
      }

      if (!this.availableTimes.includes(currentValue)) {
        this.timeValue = this.findNearestAvailableTime(currentValue);
      }
    },

    getTimeRange() {
      let start = moment('2020-04-20 00:00', 'YYYY-MM-DD H:mm');
      let end = moment('2020-04-20 23:59', 'YYYY-MM-DD H:mm');
      let nowTime = moment();
      let calendarDate = moment();

      const dateProp = this.properties.filter((item) => item.ptype_id === 'date');
      if (dateProp.length > 0 && dateProp[0].value) {
        calendarDate = moment(dateProp[0].value, this.dateFormat);
      }

      if (this.s_item_property.schedule_id !== null) {
        const activeScheduleDays = [];
        this.s_item_property.schedule_kit.days.forEach((d_item) => {
          if (d_item.days.filter((day, index) => day && calendarDate.day() === index).length > 0) {
            activeScheduleDays.push(d_item);
          }
        });

        if (activeScheduleDays.length > 0) {
          start = moment(activeScheduleDays[0].start_time, 'HH:mm:ss');
          end = moment(activeScheduleDays[0].end_time, 'HH:mm:ss');
        }
      }

      try {
        end.set('year', 2020);
        end.set('month', 1);
        end.set('date', 20);

        start.set('year', 2020);
        start.set('month', 1);
        start.set('date', 20);

        nowTime.set('year', 2020);
        nowTime.set('month', 1);
        nowTime.set('date', 20);

        if (
          dateProp.length > 0
          && dateProp[0].value === moment().format(this.dateFormat)
          && start.diff(nowTime) < 0
        ) {
          start = moment();
          end.set({
            year: moment().get('year'),
            month: moment().get('month'),
            date: moment().get('date'),
          });

          const interval = this.minuteInterval;
          const roundTime = this.roundTime(start.format('HH:mm'), interval);
          start.set({
            hour: roundTime.hour,
            minute: roundTime.min,
            second: 0,
            millisecond: 0,
          });
          start.add(interval, 'minutes');

          if (start.diff(end) > 0) {
            return false;
          }
        }

        return this.loopStartAndEndTimes(this.s_item_property, start, end);
      } catch (e) {
        return this.loopStartAndEndTimes(this.s_item_property, start, end);
      }
    },

    loopStartAndEndTimes(item, start, end) {
      if (start > end) {
        end = end.add(1, 'days');
      }

      const items = [];
      const interval = this.minuteInterval;
      for (let m = moment(start); m.isBefore(end); m.add(interval, 'minutes')) {
        items.push(m.format('H:mm'));
      }
      items.push(end.format('H:mm'));

      return items;
    },

    roundTime(time, minutesToRound) {
      let [hours, minutes] = time.split(':');
      hours = parseInt(hours, 10);
      minutes = parseInt(minutes, 10);

      const totalMinutes = (hours * 60) + minutes;
      const rounded = Math.round(totalMinutes / minutesToRound) * minutesToRound;

      return {
        hour: Math.floor(rounded / 60),
        min: rounded % 60,
      };
    },
  },
};
</script>

<style scoped>
.article-time-picker {
  width: 100%;
}
</style>
